import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header, { DEFAULT_MOBILE_LINKS } from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { useScrollReveal } from '../hooks/useScrollReveal.js';
import { useNavTone } from '../hooks/useNavTone.js';
import { openApp } from '../lib/appUrl.js';
import './Blog.css';

import featuredImg from '../assets/BB/pexels-shuaizhi-tian-485596-29900805.jpg';
import img1 from '../assets/BB/pexels-unlime-8181296.jpg';
import img2 from '../assets/BB/pexels-maxed-raw-527582413-27207885.jpg';
import img3 from '../assets/BB/pexels-tomfisk-6594779.jpg';
import img4 from '../assets/BB/pexels-keremkaplan-33124631.jpg';
import img5 from '../assets/BB/pexels-sarthak-maji-2152979497-32567868.jpg';
import img6 from '../assets/BB/pexels-gaborbalazs97-27864513.jpg';
import img7 from '../assets/BB/pexels-introspectivedsgn-35038788.jpg';
import img8 from '../assets/BB/pexels-ehaan-deva-2149036462-32358148.jpg';

// Mirrors the original's `Insights` inline-script section-tone map, used by
// useNavTone to swap the fixed nav between its light/dark treatments as the
// page scrolls.
const NAV_SECTIONS = [
  ['header', 'light'],
  ['articles', 'light'],
  ['cta', 'dark'],
];

// Matches the original " &nbsp;·&nbsp; " separator used between byline
// fragments (regular space + nbsp + middot + nbsp + regular space).
const SEP = '  ·  ';

// data-filter values on the original's .blog-filter chips, copied verbatim
// (including the "Data & Research" ampersand, encoded as `&amp;` in source).
const FILTERS = [
  { key: 'all', label: 'All articles' },
  { key: 'Advertiser Guide', label: 'Advertiser Guide' },
  { key: 'Market Insight', label: 'Market Insight' },
  { key: 'For Space Owners', label: 'For Space Owners' },
  { key: 'Data & Research', label: 'Data & Research' },
];

const FEATURED = {
  image: featuredImg,
  category: 'Advertiser Guide',
  title: 'How to plan your first OOH campaign — from brief to billboard',
  excerpt:
    "Before you pick a site, answer three questions: where does your audience move through the city, when does this message need to land, and what does the creative need to do at distance? A framework built with 50+ first-time OOH advertisers.",
  authorInitials: 'FR',
  authorName: 'FlipAd Research Team',
  date: 'May 28, 2026',
  readTime: '8 min read',
};

// Article grid, in source order. `delay` mirrors each card's `data-d`
// reveal-stagger attribute from the original markup.
const CARDS = [
  {
    image: img1,
    category: 'Market Insight',
    title: 'What makes a hoarding location worth the premium',
    authorInitials: 'AG',
    authorName: 'Ananya Gupta',
    date: 'Apr 2026',
    readTime: '4 min read',
    delay: '1',
  },
  {
    image: img2,
    category: 'For Space Owners',
    title: 'Why proof of play matters — and how to ask for it',
    authorInitials: 'FO',
    authorName: 'FlipAd Ops Team',
    date: 'Apr 2026',
    readTime: '5 min read',
    delay: '2',
  },
  {
    image: img3,
    category: 'Data & Research',
    title: 'India OOH 2026: which formats are seeing the fastest growth',
    authorInitials: 'FR',
    authorName: 'FlipAd Research Team',
    date: 'Mar 2026',
    readTime: '7 min read',
    delay: '3',
  },
  {
    image: img4,
    category: 'Advertiser Guide',
    title: 'Billboard vs. digital screen: choosing the right format for your budget',
    authorInitials: 'RV',
    authorName: 'Rohan Verma',
    date: 'Mar 2026',
    readTime: '6 min read',
    delay: '1',
  },
  {
    image: img5,
    category: 'For Space Owners',
    title: 'Pricing your site: a simple framework for first-time hosts',
    authorInitials: 'FO',
    authorName: 'FlipAd Ops Team',
    date: 'Feb 2026',
    readTime: '5 min read',
    delay: '2',
  },
  {
    image: img6,
    category: 'Market Insight',
    title: 'Why transit media is having a moment in Indian metros',
    authorInitials: 'AG',
    authorName: 'Ananya Gupta',
    date: 'Feb 2026',
    readTime: '4 min read',
    delay: '3',
  },
  {
    image: img7,
    category: 'Data & Research',
    title: 'The real cost of a billboard: a city-by-city price breakdown',
    authorInitials: 'FR',
    authorName: 'FlipAd Research Team',
    date: 'Jan 2026',
    readTime: '6 min read',
    delay: '1',
  },
  {
    image: img8,
    category: 'Advertiser Guide',
    title: 'Creative that works at 60 km/h: designing for outdoor attention spans',
    authorInitials: 'RV',
    authorName: 'Rohan Verma',
    date: 'Jan 2026',
    readTime: '5 min read',
    delay: '2',
  },
];

function FeaturedArticle({
  image,
  category,
  title,
  excerpt,
  authorInitials,
  authorName,
  date,
  readTime,
  isHidden,
}) {
  return (
    <Link
      to="/blog-article"
      className={`blog-featured reveal${isHidden ? ' is-hidden' : ''}`}
      data-cat={category}
    >
      <div
        className="blog-featured-img"
        style={{ backgroundImage: `url(${image})` }}
        role="img"
        aria-label="City billboards"
      />
      <div className="blog-featured-body">
        <span
          className="blog-card-cat"
          style={{
            width: 'fit-content',
            background: 'var(--lumen-color-action-primary-subtle)',
            borderColor: 'color-mix(in srgb,var(--lumen-color-action-primary) 22%,transparent)',
            color: 'var(--lumen-color-text-brand)',
          }}
        >
          {category}
        </span>
        <h2 className="blog-featured-title">{title}</h2>
        <p className="blog-featured-excerpt">{excerpt}</p>
        <div className="blog-read-more">
          Read article <i className="ph ph-arrow-right" aria-hidden="true" style={{ fontSize: 13 }} />
        </div>
        <div className="blog-author">
          <div className="blog-author-av" aria-hidden="true">{authorInitials}</div>
          <div>
            <div className="blog-author-name">{authorName}</div>
            <div className="blog-author-sub">{date}{SEP}{readTime}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function BlogCard({
  image,
  category,
  title,
  authorInitials,
  authorName,
  date,
  readTime,
  delay,
  isHidden,
}) {
  return (
    <Link
      to="/blog-article"
      className={`blog-card reveal${isHidden ? ' is-hidden' : ''}`}
      data-cat={category}
      data-d={delay}
    >
      <div className="blog-card-thumb" style={{ backgroundImage: `url(${image})` }}>
        <span className="blog-card-cat">{category}</span>
      </div>
      <div className="blog-card-body">
        <h3 className="blog-card-title">{title}</h3>
        <div className="blog-card-author">
          <div className="blog-card-author-av" aria-hidden="true">{authorInitials}</div>
          <div className="blog-card-byline">{authorName}{SEP}{date}{SEP}{readTime}</div>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  useScrollReveal();
  useNavTone(NAV_SECTIONS);

  const [filter, setFilter] = useState('all');

  return (
    <>
      <Seo
        title="Insights & Guides for OOH Advertising | FlipAd Blog"
        description="Advertiser guides, market insights, and data-backed research on planning, booking, and measuring out-of-home advertising campaigns in India."
        path="/blog"
      />
      <Header mobileLinks={DEFAULT_MOBILE_LINKS} activeNavKey={null} />

      {/* ════════════════════════════════════════════════
          PAGE HEADER
      ════════════════════════════════════════════════ */}
      <section className="blog-page-header" id="header">
        <div className="container">
          <div className="blog-page-header-inner reveal">
            <p className="eyebrow">Industry intelligence</p>
            <h1 className="blog-page-title">Insights from the OOH market.</h1>
            <p className="blog-page-sub">
              Guides for advertisers placing their first campaign. Playbooks for space owners
              building a listing business. Data on India&apos;s out-of-home landscape.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          ALL ARTICLES
      ════════════════════════════════════════════════ */}
      <section
        className="blog sec"
        id="articles"
        aria-label="All articles"
        style={{ paddingTop: 'var(--lumen-space-12)' }}
      >
        <div className="container">

          {/* Category filter */}
          <div className="blog-filters reveal" role="group" aria-label="Filter articles by category">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`blog-filter${filter === f.key ? ' active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Featured article */}
          <FeaturedArticle
            {...FEATURED}
            isHidden={filter !== 'all' && filter !== FEATURED.category}
          />

          {/* Article grid */}
          <div className="blog-grid">
            {CARDS.map((card) => (
              <BlogCard
                key={card.title}
                {...card}
                isHidden={filter !== 'all' && filter !== card.category}
              />
            ))}
          </div>

          <div className="blog-loadmore reveal">
            <button className="lmn-btn secondary lg" type="button">Load more articles</button>
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CLOSING CTA
      ════════════════════════════════════════════════ */}
      <section className="so-cta sec" id="cta" aria-labelledby="cta-hl">
        <div className="container">
          <div className="reveal">
            <p className="eyebrow">Get started</p>
            <h2 className="so-cta-hl" id="cta-hl">Ready to put these insights to work?</h2>
            <p className="so-cta-sub">
              1,240+ verified spaces across 23 cities. Browse for free, or list your own space in minutes.
            </p>
            <div className="so-cta-btns">
              <button type="button" className="lmn-btn primary lg" onClick={() => openApp('/inventory')}>
                Browse inventory <i className="ph ph-arrow-right" aria-hidden="true" />
              </button>
              <Link to="/space-owners" className="lmn-btn outline-white lg">
                List your space
              </Link>
            </div>
            <p className="so-cta-link">
              Have questions?{' '}
              <a
                href="mailto:support@flipad.in"
                style={{
                  color: 'color-mix(in srgb,#fff 60%,transparent)',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
              >
                Contact us →
              </a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
